import { useState, useEffect } from 'react';

const CATEGORIES_CACHE_PREFIX = 'shabu_categories_cache_';
const CATEGORIES_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Hook for fetching categories with localStorage caching
 * Reduces API calls by ~90% with 5-minute cache window
 *
 * Categories are branch-scoped now — cache key includes branchId so
 * switching branches never serves another branch's cached list.
 *
 * @param {string} apiUrl - Base API URL
 * @param {string} branchId - Currently selected branch id
 * @returns {{ categories: Array, loading: boolean, error: Error|null }}
 */
export function useCategories(apiUrl, branchId) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const cacheKey = branchId ? `${CATEGORIES_CACHE_PREFIX}${branchId}` : null;

  useEffect(() => {
    if (!branchId) {
      setCategories([]);
      setLoading(false);
      return;
    }

    const loadCategories = async () => {
      try {
        // Check cache first
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          try {
            const { data, timestamp } = JSON.parse(cached);
            if (Date.now() - timestamp < CATEGORIES_CACHE_TTL) {
              console.log('📦 Using cached categories');
              setCategories(data);
              setLoading(false);
              return;
            }
          } catch (parseError) {
            console.warn('Failed to parse cached categories, fetching fresh:', parseError);
            localStorage.removeItem(cacheKey);
          }
        }

        // Fetch fresh data — X-Branch-Id attached automatically by installBranchFetch
        console.log('🌐 Fetching fresh categories');
        const response = await fetch(`${apiUrl}/api/categories`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const result = await response.json();
        const data = result.data || result;

        // Validate data is array
        if (!Array.isArray(data)) {
          console.error('Expected array from /api/categories, got:', typeof data);
          setCategories([]);
          setLoading(false);
          return;
        }

        // Cache the result
        localStorage.setItem(cacheKey, JSON.stringify({
          data,
          timestamp: Date.now()
        }));

        setCategories(data);
      } catch (err) {
        console.error('Failed to load categories:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    loadCategories();

    // Listen for WebSocket invalidation
    const handleCategoryUpdate = () => {
      console.log('🔄 Category updated, clearing cache');
      localStorage.removeItem(cacheKey);
      loadCategories();
    };

    window.addEventListener('category:updated', handleCategoryUpdate);
    return () => window.removeEventListener('category:updated', handleCategoryUpdate);
  }, [apiUrl, branchId]);

  return { categories, loading, error };
}

/**
 * Manually clear categories cache for a branch
 * Useful for admin actions or testing
 */
export function clearCategoriesCache(branchId) {
  if (branchId) {
    localStorage.removeItem(`${CATEGORIES_CACHE_PREFIX}${branchId}`);
  } else {
    // No branchId given — clear every cached branch's list
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith(CATEGORIES_CACHE_PREFIX)) localStorage.removeItem(key);
    }
  }
  console.log('🗑️ Categories cache cleared');
}
